import React, { useState, useEffect } from "react";
import axios from 'axios';
import {
  Card,
  CardBody,
  Col,
  Row,
  Container,
  Alert,
} from "reactstrap";

// Import Breadcrumb
import Breadcrumbs from "../../components/Common/Breadcrumb";
import { useParams } from 'react-router-dom'; // Import useParams to get the user ID from the URL

const FromEditUsers = () => {
  document.title = "Edit Users | Uma Classes";
  const { id } = useParams(); // Get user ID from URL parameters

  const [formData, setFormData] = useState({
    name: "",
    standard: "",
    email: "",
    class: "",
    phoneNumber: "",
    registrationDate: "",
    expiryDate: "",
    courses: [], // Initialize courses as an empty array
  });

  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  // Fetch courses and user details on component mount
  useEffect(() => {
    const fetchCoursesAndUser = async () => {
      try {
        const token = localStorage.getItem('token');
  
        // Fetch courses
        const coursesResponse = await axios.get('https://uma.aasmo.in/api/admin/getall-category', {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        setCourses(coursesResponse || []); // Set courses from API response
  
        // Fetch user details
        const userResponse = await axios.get(`https://uma.aasmo.in/api/admin/getbyid-user/${id}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
  
        // Set user data to form state
        const userData = userResponse?.user;
        setFormData({
          name: userData?.name || "",
          standard: userData?.standard || "",
          email: userData?.email || "",
          class: userData?.class || "",
          phoneNumber: userData?.phoneNumber || "",
          courses: userData?.courses
            ?.filter(course => course?.courseId) // Filter out courses with null courseId
            .map(course => ({
              courseId: course?.courseId?._id || "",
              registrationDate: course?.registrationDate?.split("T")[0] || "",
              expiryDate: course?.expiryDate?.split("T")[0] || "",
            })) || [],
        });
  
        setLoading(false);
      } catch (error) {
        console.error("Error fetching courses or user details:", error);
        setLoading(false);
      }
    };
  
    fetchCoursesAndUser();
  }, [id]); // Fetch courses and user details when the component mounts
  

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleCourseChange = (index, field, value) => {
    const updatedCourses = [...formData.courses];
    updatedCourses[index][field] = value;
    setFormData({ ...formData, courses: updatedCourses });
  };

  const addCourse = () => {
    setFormData({
      ...formData,
      courses: [...formData.courses, { courseId: "", registrationDate: "", expiryDate: "" }],
    });
  };

  const removeCourse = (index) => {
    const updatedCourses = formData.courses.filter((_, i) => i !== index);
    setFormData({ ...formData, courses: updatedCourses });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
  
    try {
      const token = localStorage.getItem('token');
  
      // Prepare the entire data object with the courses array
      const dataToSend = {
        ...formData,
      };

      await axios.put(`https://uma.aasmo.in/api/admin/update-user/${id}`, dataToSend, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      setSuccessMessage("User edited successfully");
      setErrorMessage(""); // Clear any previous error messages
    } catch (error) {
      console.error("Error editing user:", error);
      setSuccessMessage(""); // Clear any previous success messages
      setErrorMessage("Error editing user");
    }
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid={true}>
          <Breadcrumbs title="Users" breadcrumbItem="Edit" />

          <Row>
            <Col>
              <Card>
                <CardBody>
                  <form onSubmit={handleSubmit}>
                    <Row className="mb-3">
                      <label htmlFor="name" className="col-md-2 col-form-label">
                        Full Name
                      </label>
                      <div className="col-md-10">
                        <input
                          className="form-control"
                          type="text"
                          id="name"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                        />
                      </div>
                    </Row>
                    <Row className="mb-3">
                      <label htmlFor="email" className="col-md-2 col-form-label">
                        Email
                      </label>
                      <div className="col-md-10">
                        <input
                          className="form-control"
                          type="email"
                          id="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                        />
                      </div>
                    </Row>
                    <Row className="mb-3">
                      <label htmlFor="phoneNumber" className="col-md-2 col-form-label">
                        Mobile Number
                      </label>
                      <div className="col-md-10">
                        <input
                          className="form-control"
                          type="tel"
                          id="phoneNumber"
                          name="phoneNumber"
                          value={formData.phoneNumber}
                          onChange={handleChange}
                        />
                      </div>
                    </Row>
                    <Row className="mb-3">
                      <label htmlFor="standard" className="col-md-2 col-form-label">
                        Standard
                      </label>
                      <div className="col-md-10">
                        <input
                          className="form-control"
                          type="text"
                          id="standard"
                          name="standard"
                          value={formData.standard}
                          onChange={handleChange}
                        />
                      </div>
                    </Row>
                    <Row className="mb-3">
                      <label htmlFor="class" className="col-md-2 col-form-label">
                        Class
                      </label>
                      <div className="col-md-10">
                        <input
                          className="form-control"
                          type="text"
                          id="class"
                          name="class"
                          value={formData.class}
                          onChange={handleChange}
                        />
                      </div>
                    </Row>

                    {/* Render existing courses */}
                    <h4 className="mb-3">Courses</h4>
                    {formData.courses.map((course, index) => (
                      <div key={index} className="mb-3">
                        <Row>
                          <label htmlFor={`course-${index}`} className="col-md-2 col-form-label">Course</label>
                          <div className="col-md-8">
                            <select
                              id={`course-${index}`}
                              value={course.courseId}
                              onChange={(e) => handleCourseChange(index, 'courseId', e.target.value)}
                              className="form-select"
                            >
                              <option value="">Select Course</option>
                              {courses.map(c => (
                                <option key={c._id} value={c._id}>{c.name}</option>
                              ))}
                            </select>
                          </div>
                          <div className="col-md-2">
                            <button type="button" onClick={() => removeCourse(index)} className="btn btn-danger">Remove</button>
                          </div>
                        </Row>
                        <Row className="mt-2">
                          <label htmlFor={`registrationDate-${index}`} className="col-md-2 col-form-label">Registration Date</label>
                          <div className="col-md-10">
                            <input
                              type="date"
                              className="form-control"
                              id={`registrationDate-${index}`}
                              value={course.registrationDate}
                              onChange={(e) => handleCourseChange(index, 'registrationDate', e.target.value)}
                            />
                          </div>
                        </Row>
                        <Row className="mt-2">
                          <label htmlFor={`expiryDate-${index}`} className="col-md-2 col-form-label">Expiry Date</label>
                          <div className="col-md-10">
                            <input
                              type="date"
                              className="form-control"
                              id={`expiryDate-${index}`}
                              value={course.expiryDate}
                              onChange={(e) => handleCourseChange(index, 'expiryDate', e.target.value)}
                            />
                          </div>
                        </Row>
                      </div>
                    ))}

                    <button type="button" className="btn btn-success" onClick={addCourse}>Add Course</button>

                    <div className="text-end mt-4">
                      <button type="submit" className="btn btn-primary">Submit</button>
                    </div>
                  </form>

                  {successMessage && <Alert color="success" className="mt-4">{successMessage}</Alert>}
                  {errorMessage && <Alert color="danger" className="mt-4">{errorMessage}</Alert>}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default FromEditUsers;
